import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../config';
import { databaseService } from './databaseService';
import { UserPreferences, UserRole } from '../schema';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  isAnonymous: boolean;
}

export class AuthService {
  private googleProvider: GoogleAuthProvider;

  constructor() {
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.addScope('profile');
    this.googleProvider.addScope('email');
  }

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email: string, password: string): Promise<AuthUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const authUser = this.mapFirebaseUser(userCredential.user);
      
      // Update user's last active timestamp
      await databaseService.updateUserLastActive(authUser.uid);
      
      return authUser;
    } catch (error: unknown) {
      console.error('Error signing in with email:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Create account with email and password
   */
  async createAccountWithEmail(
    email: string, 
    password: string, 
    displayName?: string
  ): Promise<AuthUser> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name if provided
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      
      const authUser = this.mapFirebaseUser(userCredential.user);
      
      // Create user profile in Firestore
      await this.createUserProfile(authUser, {
        preferredResolution: 'desktop-fhd',
        adConsent: false,
        theme: 'light'
      });
      
      return authUser;
    } catch (error: unknown) {
      console.error('Error creating account with email:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign in with Google
   */
  async signInWithGoogle(): Promise<AuthUser> {
    try {
      const userCredential = await signInWithPopup(auth, this.googleProvider);
      const authUser = this.mapFirebaseUser(userCredential.user);
      
      // Check if this is a new user and create profile if needed
      const existingProfile = await databaseService.getUserProfile(authUser.uid);
      if (!existingProfile) {
        await this.createUserProfile(authUser, {
          preferredResolution: 'desktop-fhd',
          adConsent: false,
          theme: 'light'
        });
      } else {
        // Update last active timestamp
        await databaseService.updateUserLastActive(authUser.uid);
      }
      
      return authUser;
    } catch (error: unknown) {
      console.error('Error signing in with Google:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign in anonymously
   */
  async signInAnonymously(): Promise<AuthUser> {
    try {
      const userCredential = await signInAnonymously(auth);
      const authUser = this.mapFirebaseUser(userCredential.user);
      
      // Create anonymous user profile
      await this.createUserProfile(authUser, {
        preferredResolution: 'desktop-fhd',
        adConsent: false,
        theme: 'light'
      });
      
      return authUser;
    } catch (error: unknown) {
      console.error('Error signing in anonymously:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: unknown) {
      console.error('Error signing out:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: unknown) {
      console.error('Error sending password reset email:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): AuthUser | null {
    const user = auth.currentUser;
    return user ? this.mapFirebaseUser(user) : null;
  }

  /**
   * Listen to authentication state changes
   */
  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(auth, (user) => {
      callback(user ? this.mapFirebaseUser(user) : null);
    });
  }

  /**
   * Update user profile
   */
  async updateUserProfile(updates: {
    displayName?: string;
    photoURL?: string;
  }): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      await updateProfile(user, updates);
    } catch (error: unknown) {
      console.error('Error updating user profile:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!auth.currentUser;
  }

  /**
   * Check if user is anonymous
   */
  isAnonymous(): boolean {
    return auth.currentUser?.isAnonymous ?? false;
  }

  /**
   * Convert anonymous account to permanent account
   */
  async linkWithEmail(email: string, password: string): Promise<AuthUser> {
    try {
      const user = auth.currentUser;
      if (!user || !user.isAnonymous) {
        throw new Error('No anonymous user to link');
      }

      const credential = await createUserWithEmailAndPassword(auth, email, password);
      // Note: In newer Firebase versions, you might need to use linkWithCredential
      // const linkedUser = await linkWithCredential(user, credential);
      
      const authUser = this.mapFirebaseUser(credential.user);
      
      // Update user profile in Firestore
      await databaseService.getUserProfile(authUser.uid);
      
      return authUser;
    } catch (error: unknown) {
      console.error('Error linking anonymous account:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Create user profile in Firestore
   */
  private async createUserProfile(
    authUser: AuthUser, 
    preferences: UserPreferences
  ): Promise<void> {
    try {
      await databaseService.createUserProfile(authUser.uid, {
        displayName: authUser.displayName,
        email: authUser.email,
        photoURL: authUser.photoURL,
        preferences,
        role: UserRole.USER, // Default role for new users
        createdAt: new Date(),
        lastActive: new Date()
      });
    } catch (error) {
      console.error('Error creating user profile:', error);
      // Don't throw here as auth was successful
    }
  }

  /**
   * Map Firebase user to our AuthUser interface
   */
  private mapFirebaseUser(user: FirebaseUser): AuthUser {
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      isAnonymous: user.isAnonymous
    };
  }

  /**
   * Handle Firebase auth errors and provide user-friendly messages
   */
  private handleAuthError(error: unknown): Error {
    let message = 'An authentication error occurred';

    if (error && typeof error === 'object' && 'code' in error) {
      const firebaseError = error as { code: string; message?: string };
      
      switch (firebaseError.code) {
        case 'auth/user-not-found':
          message = 'No account found with this email address';
          break;
        case 'auth/wrong-password':
          message = 'Incorrect password';
          break;
        case 'auth/email-already-in-use':
          message = 'An account with this email already exists';
          break;
        case 'auth/weak-password':
          message = 'Password should be at least 6 characters';
          break;
        case 'auth/invalid-email':
          message = 'Invalid email address';
          break;
        case 'auth/user-disabled':
          message = 'This account has been disabled';
          break;
        case 'auth/too-many-requests':
          message = 'Too many failed attempts. Please try again later';
          break;
        case 'auth/network-request-failed':
          message = 'Network error. Please check your connection';
          break;
        case 'auth/popup-closed-by-user':
          message = 'Sign-in was cancelled';
          break;
        case 'auth/popup-blocked':
          message = 'Pop-up was blocked by your browser';
          break;
        default:
          message = firebaseError.message || message;
      }
    } else if (error instanceof Error) {
      message = error.message;
    }

    return new Error(message);
  }
}

// Export singleton instance
export const authService = new AuthService();